import { useMemo } from 'react';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderRow,
  DataTableHeaderActions,
  DataTableHeaderActionsLeftSection,
  DataTableHeaderSearchInput,
  DataTableTable,
  Panel,
  PanelInset,
  PanelSection,
  PanelFooter,
  DatePicker,
  Spinner,
  EmptyState,
  EmptyStateTitle,
  EmptyStateDescription,
  Paragraph,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getLocalTimeZone, today } from '@internationalized/date';
import type { DeliverabilityMetricRow, DeliverabilityDateRange } from '../types';
import { MetricCell } from './metric-cell';
import { DeliverabilityRatePopover } from './deliverability-rate-popover';
import { FIRST_POSSIBLE_DATE, NO_SPAM_PROVIDERS } from '../utils/constants';
import { getQualityIndicator, getQualityColor } from '../utils/quality-indicators';

const columnHelper = createColumnHelper<DeliverabilityMetricRow>();

interface DeliverabilityTableProps {
  data: DeliverabilityMetricRow[];
  dateRange: DeliverabilityDateRange;
  onDateRangeChange: (range: DeliverabilityDateRange) => void;
  isLoading: boolean;
  isError: boolean;
  sorting: { id: string; desc: boolean };
  onSortingChange: (sorting: { id: string; desc: boolean } | null) => void;
  search: string;
  onSearchChange: (search: string) => void;
  hasNoData: boolean;
}

function formatPercent(value: number, precision: number): string {
  const percent = value * 100;
  const threshold = Math.pow(10, -precision);

  if (percent > 0 && percent < threshold) {
    return `<${threshold.toFixed(precision)}%`;
  }

  return `${percent.toFixed(precision)}%`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function DeliverabilityTable({
  data,
  dateRange,
  onDateRangeChange,
  isLoading,
  isError,
  _sorting,
  _onSortingChange,
  search,
  onSearchChange,
  hasNoData,
}: DeliverabilityTableProps) {
  const todayDate = today(getLocalTimeZone());

  // Handle date picker change
  const handleDatePickerChange = (value: unknown) => {
    if (value != null && typeof value === 'object' && 'start' in value && 'end' in value) {
      onDateRangeChange(value as DeliverabilityDateRange);
    }
  };

  // Define columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('provider', {
        header: 'Provider',
        cell: (info) => (
          <Text text="product-p" weight="medium" truncate>
            {info.getValue()}
          </Text>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('sentPercentage', {
        header: 'Outbound',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => (
          <MetricCell percent={info.getValue()} count={info.row.original.sentCount} metricName="deliveryRate" />
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('deliveryRate', {
        header: 'Delivered',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => (
          <MetricCell percent={info.getValue()} count={info.row.original.deliveryCount} metricName="deliveryRate" />
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('bounceRate', {
        header: 'Bounced',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => (
          <MetricCell
            percent={info.getValue()}
            count={info.row.original.bounceCount}
            metricName="bounceRate"
            isInverse
          />
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('openRate', {
        header: 'Opened',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => {
          const quality = getQualityIndicator('openRate', info.getValue());
          const color = getQualityColor(quality);
          const formattedPercent = formatPercent(info.getValue(), 1);
          const formattedCount = formatNumber(info.row.original.openCount);

          return (
            <DeliverabilityRatePopover
              isClick={false}
              row={info.row.original}
              formattedPercent={formattedPercent}
              formattedCount={formattedCount}
              color={color}
            />
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor('clickRate', {
        header: 'Clicked',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => {
          const quality = getQualityIndicator('clickRate', info.getValue());
          const color = getQualityColor(quality);
          const formattedPercent = formatPercent(info.getValue(), 1);
          const formattedCount = formatNumber(info.row.original.clickCount);

          return (
            <DeliverabilityRatePopover
              isClick={true}
              row={info.row.original}
              formattedPercent={formattedPercent}
              formattedCount={formattedCount}
              color={color}
            />
          );
        },
        enableSorting: true,
      }),
      columnHelper.accessor('spamRate', {
        header: 'Spammed',
        meta: { width: 120, align: 'end' as const },
        cell: (info) => {
          const { provider } = info.row.original;
          if (NO_SPAM_PROVIDERS.includes(provider as (typeof NO_SPAM_PROVIDERS)[number])) {
            return (
              <Tooltip placement="right" content={`${provider} does not report spam`}>
                <span>—</span>
              </Tooltip>
            );
          }
          return (
            <MetricCell percent={info.getValue()} count={info.row.original.spamCount} metricName="spamRate" isInverse />
          );
        },
        enableSorting: true,
      }),
    ],
    [],
  );

  // Filter data by search
  const filteredData = useMemo(() => {
    if (search.trim() === '') return data;
    const lowerSearch = search.toLowerCase();
    return data.filter((row) => row.provider.toLowerCase().includes(lowerSearch));
  }, [data, search]);

  const shouldShowTable = !isLoading && !isError && !hasNoData;

  return (
    <DataTable caption="Inbox Providers" data={filteredData} columns={columns}>
      <Panel>
        <DataTableHeader>
          <DataTableHeaderRow>
            <DataTableHeaderActions>
              <DataTableHeaderActionsLeftSection>
                <DatePicker
                  ariaLabel="Date range"
                  value={dateRange}
                  onChange={handleDatePickerChange}
                  isRange
                  withPresets
                  minDate={FIRST_POSSIBLE_DATE}
                  maxDate={todayDate}
                  size="sm"
                />
                <DataTableHeaderSearchInput
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onClear={() => onSearchChange('')}
                />
              </DataTableHeaderActionsLeftSection>
            </DataTableHeaderActions>
          </DataTableHeaderRow>
        </DataTableHeader>

        <PanelInset>{shouldShowTable ? <DataTableTable /> : null}</PanelInset>

        {isLoading ? (
          <PanelSection display="flex" justifyContent="center" alignItems="center" p="400">
            <Spinner />
          </PanelSection>
        ) : null}

        {isError ? (
          <PanelSection>
            <EmptyState>
              <EmptyStateTitle icon="error">Error loading data</EmptyStateTitle>
              <EmptyStateDescription>
                We encountered an error while loading the data. Please try again later.
              </EmptyStateDescription>
            </EmptyState>
          </PanelSection>
        ) : null}

        {hasNoData && !isLoading && !isError ? (
          <PanelSection>
            <EmptyState>
              <EmptyStateTitle icon="analysis">No data available</EmptyStateTitle>
              <EmptyStateDescription>
                No deliverability data available for the selected date range.
              </EmptyStateDescription>
            </EmptyState>
          </PanelSection>
        ) : null}

        <PanelFooter withBorder>
          <Paragraph variant="secondary">Deliverability information is available starting April 15th, 2025</Paragraph>
        </PanelFooter>
      </Panel>
    </DataTable>
  );
}
